import filterData from './filter-data';
import filterValues from './filter-values';

// Config.
import serviceTypes from '@/config/service-types';
import sources from '@/config/sources';
import sourcesOfFunds from '@/config/sources-of-funds';

/**
 * Given a config array, a series object, a filter object, and a disambiguated
 * filter object, returns an array of data objects containing filtered data
 * points and labels. Essentially, applies filters to data.
 * @param {Array} config - An array of configuration data points.
 * @param {object} series - A series object with individual series as keys.
 * @param {object} filters - Filters to apply to the data.
 * @param {object} disambiguatedFilters - Disambiguated filters to apply to the data.
 * @returns {Array} An array of objects describing data.
 */
const getData = (config, series, filters, disambiguatedFilters) => {
  const {
    outputType,
    serviceType,
    sourceOfFunds,
    tab,
    yearType,
  } = filters;
  const {
    yearCompare,
    yearRange,
    yearSingle,
  } = disambiguatedFilters;

  // Determine the structure of the data - multiple service types vs. multiple sources of funds.
  const multipleServiceTypes = tab % 2 === 0;

  return (multipleServiceTypes ? serviceType : sourceOfFunds).map((slug) => {
    let data;

    // Fork for fetching multiple service types vs. multiple sources of funds.
    if (multipleServiceTypes) {
      data = filterData(series[slug], ...sources.get(sourceOfFunds[0]));
    } else {
      data = filterData(series[serviceType[0]], ...sources.get(slug));
    }

    // Apply date filters.
    if (yearType === 'range') {
      data = data.filter(({ year }) => year >= yearRange[0] && year <= yearRange[1]);
    } else if (yearType === 'compare') {
      data = data.filter(({ year }) => yearCompare.includes(year));
    } else if (yearType === 'single') {
      data = data.filter(({ year }) => yearSingle === year);
    }

    // If we are on tab 3 or 4, divide data points by index values to yield percentages.
    if (tab >= 2) {
      const index = tab === 2
        ? filterData(series.allTypes, ...sources.get(sourceOfFunds[0]))
        : filterData(series[serviceType[0]], ...sources.get('allSources'));
      data = data.map((datum) => ({
        ...datum,
        value: datum.value / index.find((check) => check.year === datum.year).value,
      }));
    }

    return {
      data: filterValues(config, data, tab >= 2 ? '%' : outputType),
      label: multipleServiceTypes ? serviceTypes.get(slug) : sourcesOfFunds.get(slug),
    };
  });
};

export default getData;
